import React, { useState, useEffect, useContext } from 'react';
import { Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, Link } from "react-router-dom";
import { numberFormat, unitNameByProject } from '../helpers/GlobalHelper';
import { AppContext } from '../context/AppContext'

// Define a type for the company data
interface CompanyData {
    idCompanies: number;
    idAccLedgers: number;
    companyName: string;
    openingAmount: number;
    totalActualInvoicedAmount: number;
    totalActualPaymentAmount: number;
    totalSalesOrder: number;
    totalShipmentAmount: number;
    totalInvoicedAmount: number;
    totalCollectionAmount: number;
    totalBalance: number;
    totalDeliveredQuantity: number;
    companyAssignedSrName: string;
}

const SalesReportV2: React.FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const { currentUser } = useContext(AppContext);
    const [loading, setLoading] = useState(false);
    const [salesData, setSalesData] = useState<CompanyData[]>([]);
    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        idSrCategories: query.get('idSrCategories') ?? '',
        idSr: query.get('idSr') ?? currentUser?.id_roles===1 ? currentUser?.id : '', // if admin, then empty else current user
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? '',
    });

    const [projects, setProjects] = useState<{ label: string; value: string }[]>([]);
    const [project, setProject] = useState<{ label: string; value: string }>({ label: 'Select Project', value: '' });

    
    const [categories, setCategories] = useState([])
    const [category, setCategory] = useState({ label: 'Select SR Category', value: '' });

    const [sr, setSr] = useState({ label: 'Select SR', value: '' });
    const [salesRepresentatives, setSalesRepresentatives] = useState([]);
    
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    useEffect(() => {
        getProjects();
        getAllSalesRepresentative()
    }, []);

    // Ensure query idSrCategories is captured on mount
    useEffect(() => {
        const qsId = query.get('idSrCategories');
        if (qsId !== null) {
            setFormData((prev) => ({ ...prev, idSrCategories: qsId }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const qsProjectId = query.get('idProjects');
            const selected =
                qsProjectId && projects.find((p) => String(p.value) === String(qsProjectId))
                    ? (projects.find((p) => String(p.value) === String(qsProjectId)) as {label:string; value:string})
                    : projects[0];

            setProject(selected);
            setFormData((prev) => ({
                ...prev,
                idProjects: selected.value,
            }));

            getCategories(selected.value);
        }

        if (formData.fromDate && formData.toDate && formData.idProjects) {
            handleViewReport();
        }
    }, [projects]); // eslint-disable-line react-hooks/exhaustive-deps

    // Removed auto-sync effect that was overwriting query-selected category.
    // Category changes are now handled explicitly in handleCategoryChange.

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                let projectObj = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectObj);
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    const getAllSalesRepresentative = () => {
        
        fetch(
            process.env.REACT_APP_API_URL + 'getAllSr',
            getRequestOptions()
        )
            .then((resp) => {
                return resp.json()
            })
            .then((resp) => {
                let users = resp.map((item: any) => {
                    const capitalizedStatus = item.status.charAt(0).toUpperCase() + item.status.slice(1); // e.g., 'active' -> 'Active'
                    item.label = `${item.first_name} ${item.last_name} (${item.assigned_projects} projects, ${capitalizedStatus})`;
                    item.value = item.id;
                    return item
                })
                setSalesRepresentatives(users);

            })
            .catch((error) => {
                console.log(error, 'catch the hoop')
            })


    }

    const handleProjectChange = (project: any) => {
        setProject(project);
        setFormData({ ...formData, idProjects: project ? project.value : '' });
        getCategories(project.value)
    };


    const getCategories = async (idProjects: any) => {
        
        const url = process.env.REACT_APP_API_URL + 'allSrCategory?idProjects=' + idProjects
        const response = await fetch(url, getRequestOptions())
        const data = await response.json()

        data.map((element: any) => {
            element.value = element.id_sr_categories;
            element.label = element.sr_category_name;
            return element;
        })

        // Try to preselect from query if provided
        const querySelected =
            formData.idSrCategories
                ? data.find((c: any) => Number(c.value) === Number(formData.idSrCategories))
                : null;

        // console.debug('querySelected', querySelected, 'data', data, 'formData', formData);

        var allCategoriesOpt = [{ id: '', name: '', label: 'All', value: '' }]
        let allCategories = data.concat(allCategoriesOpt);
        setCategories(allCategories);

        if (querySelected) {
            setCategory(querySelected);
            setFormData((prev) => ({ ...prev, idSrCategories: querySelected.value }));
            return;
        }

        if (data.length === 1) {
            setCategory(data[0]);
            setFormData((prev) => ({ ...prev, idSrCategories: data[0].value }));
            return;
        }

        // categories list exists but no specific selection -> default to "All"
        const allOpt = allCategories.find((c: any) => c.value === '') || allCategoriesOpt[0];
        setCategory(allOpt);
        setFormData((prev) => ({ ...prev, idSrCategories: '' }));
    }

    const handleCategoryChange = (category: any) => {
        setCategory(category);
        if(category){
            setFormData({ ...formData, idSrCategories: category.value });
        }else{
            setFormData({ ...formData, idSrCategories: '' });
        }
    }

    const handleSrChange = (selectedOption: any) => {
        if (!selectedOption) {
            selectedOption = { label: 'Select SR', value: '' };
        }

        setSr(selectedOption);
        setFormData({ ...formData, idSr: selectedOption.value });
    };
    
    
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleViewReport = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/sales-v2',
                postRequestOptions(formData)
            );
            const data = await response.json();
            if (data.success) {
                setSalesData(data.data);
            } else {
                setSalesData([]);
                const errors: string[] = Array.isArray(data?.errors)
                    ? data.errors
                    : data?.errorMessage
                    ? [data.errorMessage]
                    : ['Failed to load report.'];
                errors.forEach((error: string) =>
                    toast.error(error, { position: 'bottom-right', autoClose: 4000 })
                );
            }
        } catch (error) {
            console.error('Error submitting data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            // Toggle direction if same column
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New column, default to ascending
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const getSortedData = () => {
        if (!sortColumn) return salesData;

        const sorted = [...salesData].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortColumn) {
                case 'idCompanies':
                    aValue = a.idCompanies;
                    bValue = b.idCompanies;
                    break;
                case 'companyName':
                    aValue = a.companyName?.toLowerCase() || '';
                    bValue = b.companyName?.toLowerCase() || '';
                    break;
                case 'openingAmount':
                    aValue = Number(a.openingAmount) || 0;
                    bValue = Number(b.openingAmount) || 0;
                    break;
                case 'totalInvoicedAmount':
                    aValue = Number(a.totalInvoicedAmount) || 0;
                    bValue = Number(b.totalInvoicedAmount) || 0;
                    break;
                case 'totalCollectionAmount':
                    aValue = Number(a.totalCollectionAmount) || 0;
                    bValue = Number(b.totalCollectionAmount) || 0;
                    break;
                case 'totalBalance':
                    aValue = Number(a.totalBalance) || 0;
                    bValue = Number(b.totalBalance) || 0;
                    break;
                case 'totalDeliveredQuantity':
                    aValue = Number(a.totalDeliveredQuantity) || 0;
                    bValue = Number(b.totalDeliveredQuantity) || 0;
                    break;
                case 'companyAssignedSrName':
                    aValue = a.companyAssignedSrName?.toLowerCase() || '';
                    bValue = b.companyAssignedSrName?.toLowerCase() || '';
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    };

    const getSortIcon = (column: string) => {
        if (sortColumn !== column) {
            return <i className="fas fa-sort text-muted ms-1" style={{ fontSize: '0.8em' }}></i>;
        }
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1" style={{ fontSize: '0.8em' }}></i>
            : <i className="fas fa-sort-down text-primary ms-1" style={{ fontSize: '0.8em' }}></i>;
    };

    const sortedData = getSortedData();
    const queryParams = new URLSearchParams(formData);

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Title></Card.Title>
                    <div>
                        <div className='row'>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Select SR Category</Form.Label>
                                    <Select
                                        value={category}
                                        onChange={handleCategoryChange}
                                        options={categories}
                                        placeholder={category.label}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Form.Group>
                            </div>
                            {currentUser?.id_roles !== 1 &&
                                <div className='col-md-2'>
                                    <Form.Group className="mb-3" controlId="project">
                                        <Form.Label>Select SR</Form.Label>
                                        <Select
                                            value={sr}
                                            onChange={handleSrChange}
                                            options={salesRepresentatives}
                                            menuPosition="fixed"
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                            isClearable={true}
                                        />
                                    </Form.Group>
                                </div>
                            }
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control type="date" placeholder="From Date" name='fromDate' value={formData.fromDate} onChange={handleChange}/>
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control type="date" placeholder="To Date" name='toDate'  value={formData.toDate} onChange={handleChange}/>
                                </Form.Group>
                            </div>
                            <div className='col-md-1'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary w-100 p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        )}
                                        <span>View Report</span>
                                    </button>
                                </div>                                
                            </div>
                            <div className='col-md-1'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <a className="btn btn-sm btn-primary w-100 p-3 mb-2" href={`${process.env.REACT_APP_BACKEND}sales_report_v2_download?` + queryParams.toString()}><i className='fas fa-download'></i> Download</a>
                                </div>                                
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <div className="row">
                <div className="col-12">
                    <Table responsive bordered hover>
                        <thead>
                            <tr>
                                <th 
                                    className="text-center" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('idCompanies')}
                                >
                                    ID {getSortIcon('idCompanies')}
                                </th>
                                <th 
                                    className="text-center" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('companyName')}
                                >
                                    Client Name {getSortIcon('companyName')}
                                </th>
                                <th 
                                    className="text-center" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('openingAmount')}
                                >
                                    Op. Balance {getSortIcon('openingAmount')}
                                </th>
                                <th 
                                    className="text-center" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('totalInvoicedAmount')}
                                >
                                    Sales {getSortIcon('totalInvoicedAmount')}
                                </th>
                                <th 
                                    className="text-center" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('totalCollectionAmount')}
                                >
                                    Collection {getSortIcon('totalCollectionAmount')}
                                </th>
                                <th 
                                    className="text-center" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('totalBalance')}
                                >
                                    Balance {getSortIcon('totalBalance')}
                                </th>
                                <th 
                                    className='text-center' 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('totalDeliveredQuantity')}
                                >
                                    Qty( {unitNameByProject(project?.value)} ) {getSortIcon('totalDeliveredQuantity')}
                                </th>
                                <th 
                                    className="text-center" 
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleSort('companyAssignedSrName')}
                                >
                                    Sales Person {getSortIcon('companyAssignedSrName')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.length > 0 ? (
                                sortedData.map((company, index) => (
                                    <tr key={company.idCompanies}>
                                        <td className="text-center">{company.idCompanies}</td>
                                        <td>
                                            <Link
                                                to={`reports?type=ledgerStatement&idProjects=${formData.idProjects}&idAccLedgers=${company.idAccLedgers}&fromDate=${formData.fromDate}&toDate=${formData.toDate}`}
                                                target="_blank"
                                            >
                                                {company.companyName}
                                            </Link>
                                        </td>
                                        <td className="text-end">{numberFormat(company.openingAmount)}</td>
                                        <td className="text-end">{numberFormat(company.totalInvoicedAmount)}</td>
                                        <td className="text-end">{numberFormat(company.totalCollectionAmount)}</td>
                                        <td className="text-end">{numberFormat(company.totalBalance)}</td>
                                        <td className='text-end'>{numberFormat(company.totalDeliveredQuantity)}</td>
                                        <td>{company.companyAssignedSrName}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={10} className="text-center">
                                        No Data Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={2} className="text-center"><strong>Total</strong></td>
                                <td className="text-end"><strong>{numberFormat(salesData.reduce((a, b) => a + Number(b.openingAmount), 0))}</strong></td>
                                <td className="text-end"><strong>{numberFormat(salesData.reduce((a, b) => a + Number(b.totalInvoicedAmount), 0))}</strong></td>
                                <td className="text-end"><strong>{numberFormat(salesData.reduce((a, b) => a + Number(b.totalCollectionAmount), 0))}</strong></td>
                                <td className="text-end"><strong>{numberFormat(salesData.reduce((a, b) => a + Number(b.totalBalance), 0))}</strong></td>
                                <td className="text-end"><strong>{numberFormat(salesData.reduce((a, b) => a + Number(b.totalDeliveredQuantity), 0))}</strong></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </Table>
                </div>
            </div>
        </>
    );
};

export default SalesReportV2;
